import { Box, Button, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCompanyController } from 'api/controllers/CompanyController';
import { useInvoiceController } from 'api/controllers/InvoiceController';

import {
  Company,
  CurrencyDropDown,
  EntryType,
  Invoice,
  InvoiceConfidence,
  InvoiceExpenseType,
  InvoicePatch,
  InvoiceSection
} from 'openapi';

import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import { useInvoiceSectionFocus } from 'hooks/useInvoiceSectionFocus';

import { getConfigByDocumentType } from 'utils/config/documentEntries';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import {
  CONFIDENCE_SECTIONS,
  getActiveSectionIds,
  getRequiredFieldsForSection,
  SECTION_FIELDS,
  SectionId
} from 'utils/constants/invoiceVerificationSections';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import {
  getFieldConfidenceKey,
  getInvoiceHasConfidenceData,
  getOverallConfidence,
  getSectionConfidence,
  getSectionShouldAutoExpand,
  OverallConfidence
} from 'utils/helpers/confidenceHelpers';
import { getSendForApprovalDefaultValues } from 'utils/helpers/invoiceHelpers';
import { SendForApprovalFormValues } from 'utils/interfaces/InvoiceProps';
import {
  mapInvoiceForReceiverSubmit,
  mapInvoiceForSendForApprovalSubmit
} from 'utils/mappers/invoice';

import { verificationContentWrapper } from 'styles/pages/InvoiceVerificationStyle';

import { InvoiceFilePreview } from './InvoiceFilePreview';
import { CommentsAndTagsCard } from './InvoiceVerificationCards/CommentsAndTagsCard';
import { CounterpartyCard } from './InvoiceVerificationCards/CounterpartyCard/CounterpartyCard';
import { DocumentDataCard } from './InvoiceVerificationCards/DocumentDataCard';
import { DocumentPaymentStatusSection } from './InvoiceVerificationCards/DocumentPaymentStatusSection';
import { InitiationCard } from './InvoiceVerificationCards/InitiationCard';
import { LineItemsCard } from './InvoiceVerificationCards/LineItemsCard';
import { OverallConfidenceBanner } from './InvoiceVerificationCards/OverallConfidenceBanner';
import { ReimbursementCard } from './InvoiceVerificationCards/ReimbursementCard/ReimbursementCard';

interface FileInvoiceVerificationProps {
  currentInvoice: Invoice;
  onCompanyChange?: (receiverValues: InvoicePatch) => void;
  /** Called when the line items autosave returns the authoritative invoice. */
  onInvoiceUpdated?: (invoice: Invoice) => void;
}

const isMissingRequiredValue = (value: unknown): boolean =>
  value === undefined || value === null || value === '';

// An invoice refresh (e.g. the SSE update fired after resolving a counterparty
// inline) returns a brand new confidence object with the very same scores. Only
// a new AI scan actually changes the scores, so compare by value to tell the two
// apart instead of relying on object identity.
const areConfidencesEqual = (
  first: InvoiceConfidence | undefined,
  second: InvoiceConfidence | undefined
): boolean => {
  if (first === second) {
    return true;
  }
  if (!first || !second) {
    return false;
  }

  const keys = new Set([...Object.keys(first), ...Object.keys(second)]) as Set<
    keyof InvoiceConfidence
  >;

  return [...keys].every(
    (key) => (first[key] ?? null) === (second[key] ?? null)
  );
};

export const FileInvoiceVerification = ({
  currentInvoice,
  onCompanyChange,
  onInvoiceUpdated
}: FileInvoiceVerificationProps) => {
  const { translate } = useTranslations();
  const { sendInvoiceForApprovalById, saveInvoiceSectionData } =
    useInvoiceController();
  const { getAllCompanies } = useCompanyController();
  const { getCurrencies, getExpenseTypes } = useAdditionalFieldsController();

  const methods = useForm<SendForApprovalFormValues>({
    mode: 'onChange',
    defaultValues: getSendForApprovalDefaultValues(currentInvoice)
  });

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { isValid, isSubmitting, errors, dirtyFields }
  } = methods;

  const formValues = watch();

  const isReimbursement = watch(InvoiceVerificationFieldName.IsReimbursement);
  const reimbursementCounterpartyId = watch(
    InvoiceVerificationFieldName.ReimbursementCounterpartyId
  );

  useEffect(() => {
    if (!isReimbursement) {
      return;
    }

    if (getValues(InvoiceVerificationFieldName.IsPrepaid)) {
      setValue(InvoiceVerificationFieldName.IsPrepaid, false);
    }
    if (getValues(InvoiceVerificationFieldName.PaymentChannel) !== undefined) {
      setValue(InvoiceVerificationFieldName.PaymentChannel, undefined);
    }
  }, [isReimbursement, getValues, setValue]);

  const dirtyFieldNames = Object.keys(dirtyFields);

  const handleReceiverChange = useCallback(
    (newCompanyId: number) => {
      onCompanyChange?.(
        mapInvoiceForReceiverSubmit({
          ...methods.getValues(),
          companyId: newCompanyId
        } as InvoicePatch)
      );
    },
    [methods, onCompanyChange]
  );

  const previousCompanyIdRef = useRef(currentInvoice.companyId);

  useEffect(() => {
    if (previousCompanyIdRef.current === currentInvoice.companyId) {
      return;
    }
    previousCompanyIdRef.current = currentInvoice.companyId;
    methods.reset(getSendForApprovalDefaultValues(currentInvoice));
    methods.setValue(InvoiceVerificationFieldName.ExpenseType, '', {
      shouldValidate: true
    });
  }, [currentInvoice, methods]);

  // Fields that contribute to the overall confidence score. The reimbursement
  // participant has no confidence key of its own, so once a counterparty is
  // provided the section is treated as confident (1.0) rather than "needs
  // review", which would otherwise leave the banner amber at 100%.
  const confidenceScoringFieldNames = useMemo(() => {
    const scored = dirtyFieldNames.filter(
      (fieldName) => getFieldConfidenceKey(fieldName) !== undefined
    );
    if (isReimbursement && reimbursementCounterpartyId) {
      scored.push(InvoiceVerificationFieldName.ReimbursementCounterpartyId);
    }
    return scored;
  }, [dirtyFieldNames, isReimbursement, reimbursementCounterpartyId]);

  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState<
    InvoiceExpenseType[]
  >([]);
  const [currencies, setCurrencies] = useState<CurrencyDropDown[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // The expense type picked in the Document Data form, before it is persisted
  // on Send for Approval. Follows the server value whenever the invoice is
  // refreshed so a stale selection never overrides fresh server state.
  const [liveExpenseTypeId, setLiveExpenseTypeId] = useState<
    number | string | null | undefined
  >(undefined);

  const documentConfig = useMemo(
    () => getConfigByDocumentType(currentInvoice.entryType as EntryType),
    [currentInvoice.entryType]
  );

  const serverExpenseTypeId = currentInvoice.expenseType?.id;

  useEffect(() => {
    setLiveExpenseTypeId(serverExpenseTypeId);
  }, [currentInvoice.id, serverExpenseTypeId]);

  // The synthetic line item mirrors the Document amount live, but only for
  // EUR documents: the exchange rate is server-side only, so for other
  // currencies the row keeps the persisted invoiceAmountEur until re-save.
  const watchedInvoiceAmount = watch(
    InvoiceVerificationFieldName.InvoiceAmount
  );
  const watchedCurrencyId = watch(InvoiceVerificationFieldName.CurrencyId);

  const effectiveCurrencyId = watchedCurrencyId ?? currentInvoice.currencyId;
  const isEurDocument = currencies.length
    ? currencies.find(
        (currency) => String(currency.id) === String(effectiveCurrencyId)
      )?.isoCode === 'EUR'
    : currentInvoice.currency === 'EUR';

  const parsedInvoiceAmount = Number(watchedInvoiceAmount);
  const liveDocumentAmountEur = isEurDocument
    ? watchedInvoiceAmount !== '' && Number.isFinite(parsedInvoiceAmount)
      ? parsedInvoiceAmount
      : null
    : undefined;

  const hasConfidenceData = getInvoiceHasConfidenceData(currentInvoice);

  const missingRequiredFieldNames = useMemo(() => {
    const missing: string[] = [];

    getActiveSectionIds(Boolean(formValues.isReimbursement)).forEach(
      (sectionId) => {
        getRequiredFieldsForSection(
          sectionId,
          documentConfig.allowExpenseType
        ).forEach((fieldName) => {
          if (
            isMissingRequiredValue(
              formValues[fieldName as keyof SendForApprovalFormValues]
            )
          ) {
            missing.push(fieldName);
          }
        });
      }
    );

    return missing;
  }, [formValues, documentConfig.allowExpenseType]);

  const overallConfidence = useMemo<OverallConfidence | null>(() => {
    if (!hasConfidenceData) {
      return null;
    }
    return getOverallConfidence(
      {
        ...currentInvoice,
        isReimbursement,
        reimbursementCounterpartyId
      },
      CONFIDENCE_SECTIONS,
      confidenceScoringFieldNames,
      Object.keys(errors),
      missingRequiredFieldNames
    );
  }, [
    hasConfidenceData,
    currentInvoice,
    isReimbursement,
    reimbursementCounterpartyId,
    confidenceScoringFieldNames,
    errors,
    missingRequiredFieldNames
  ]);

  const initiationConfidence = useMemo(
    () =>
      getSectionConfidence(
        currentInvoice,
        SECTION_FIELDS.initiation,
        dirtyFieldNames
      ),
    [currentInvoice, dirtyFieldNames]
  );

  const counterpartyConfidence = useMemo(
    () =>
      getSectionConfidence(
        currentInvoice,
        SECTION_FIELDS.counterparty,
        dirtyFieldNames
      ),
    [currentInvoice, dirtyFieldNames]
  );

  const documentDataConfidence = useMemo(
    () =>
      getSectionConfidence(
        currentInvoice,
        SECTION_FIELDS.documentData,
        dirtyFieldNames
      ),
    [currentInvoice, dirtyFieldNames]
  );

  // The reimbursement participant is picked manually and has no AI confidence
  // key of its own. Report LOW while it is missing so the section still needs
  // review, and no level once it is filled so the card shows its validity
  // checkmark instead of a misleading confidence icon.
  const reimbursementConfidence = useMemo<ConfidenceLevel | undefined>(() => {
    if (!isReimbursement || reimbursementCounterpartyId) {
      return undefined;
    }
    return ConfidenceLevel.LOW;
  }, [isReimbursement, reimbursementCounterpartyId]);

  const [userToggledSections, setUserToggledSections] = useState<
    Partial<Record<SectionId, boolean>>
  >({});

  const [autoExpandedSections, setAutoExpandedSections] = useState<
    Partial<Record<SectionId, boolean>>
  >({});

  const previousConfidenceRef = useRef(currentInvoice.confidence);

  useEffect(() => {
    if (
      !areConfidencesEqual(
        currentInvoice.confidence,
        previousConfidenceRef.current
      )
    ) {
      setUserToggledSections({});
      setAutoExpandedSections({});
    }
    previousConfidenceRef.current = currentInvoice.confidence;
  }, [currentInvoice.confidence]);

  const sectionConfidence: Record<SectionId, ConfidenceLevel> = useMemo(
    () => ({
      initiation: initiationConfidence,
      documentData: documentDataConfidence,
      counterparty: counterpartyConfidence,
      reimbursement: getSectionConfidence(
        currentInvoice,
        SECTION_FIELDS.reimbursement,
        dirtyFieldNames
      ),
      // The line items section has no confidence-scored fields. Its review
      // state comes from the server-computed match flag, mirroring the
      // mismatch logic inside LineItemsCard, so the section auto-expands
      // like the other sections when the amounts need review.
      lineItems:
        (currentInvoice.products?.length ?? 0) > 0 &&
        currentInvoice.lineItemsMatchDocumentAmount === false
          ? ConfidenceLevel.MEDIUM
          : ConfidenceLevel.HIGH
    }),
    [
      initiationConfidence,
      documentDataConfidence,
      counterpartyConfidence,
      currentInvoice,
      dirtyFieldNames
    ]
  );

  const sectionHasError = useMemo(() => {
    const result: Record<SectionId, boolean> = {
      initiation: false,
      documentData: false,
      counterparty: false,
      reimbursement: false,
      lineItems: false
    };

    (Object.keys(SECTION_FIELDS) as SectionId[]).forEach((sectionId) => {
      const isReimbursementActive = formValues.isReimbursement;
      if (sectionId === 'reimbursement' && !isReimbursementActive) {
        result[sectionId] = false;
        return;
      }

      const hasValidationError = SECTION_FIELDS[sectionId].some(
        (fieldName) => !!errors[fieldName as keyof SendForApprovalFormValues]
      );

      const requiredFields = getRequiredFieldsForSection(
        sectionId,
        documentConfig.allowExpenseType
      );

      const hasMissingRequired = requiredFields.some((fieldName) =>
        isMissingRequiredValue(
          formValues[fieldName as keyof SendForApprovalFormValues]
        )
      );

      result[sectionId] = hasValidationError || hasMissingRequired;
    });

    return result;
  }, [errors, formValues, documentConfig.allowExpenseType]);

  const sectionShouldAutoExpand: Record<SectionId, boolean> = useMemo(() => {
    const result: Record<SectionId, boolean> = {
      initiation: false,
      documentData: false,
      counterparty: false,
      reimbursement: false,
      lineItems: false
    };

    (Object.keys(SECTION_FIELDS) as SectionId[]).forEach((sectionId) => {
      if (sectionId === 'reimbursement' && !formValues.isReimbursement) {
        result[sectionId] = false;
        return;
      }

      result[sectionId] = getSectionShouldAutoExpand(
        sectionConfidence[sectionId],
        sectionHasError[sectionId]
      );
    });

    return result;
  }, [sectionConfidence, sectionHasError, formValues.isReimbursement]);

  useEffect(() => {
    setAutoExpandedSections((prev) => {
      let changed = false;
      const next = { ...prev };

      (Object.keys(SECTION_FIELDS) as SectionId[]).forEach((sectionId) => {
        const isActive =
          sectionId !== 'reimbursement' || formValues.isReimbursement;

        if (!isActive) {
          if (next[sectionId]) {
            next[sectionId] = false;
            changed = true;
          }
          return;
        }

        if (sectionShouldAutoExpand[sectionId] && !next[sectionId]) {
          next[sectionId] = true;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [sectionShouldAutoExpand, formValues.isReimbursement]);

  const sectionExpanded: Record<SectionId, boolean> = useMemo(() => {
    const result: Record<SectionId, boolean> = {
      initiation: false,
      documentData: false,
      counterparty: false,
      reimbursement: false,
      lineItems: false
    };

    (Object.keys(SECTION_FIELDS) as SectionId[]).forEach((sectionId) => {
      const manualOverride = userToggledSections[sectionId];
      if (manualOverride !== undefined) {
        result[sectionId] = manualOverride;
        return;
      }

      if (sectionShouldAutoExpand[sectionId]) {
        result[sectionId] = true;
        return;
      }

      result[sectionId] = Boolean(autoExpandedSections[sectionId]);
    });

    return result;
  }, [userToggledSections, autoExpandedSections, sectionShouldAutoExpand]);

  const {
    getSectionRef,
    handleSectionExpansionChange,
    handleSectionExpanded,
    handleSectionOpened
  } = useInvoiceSectionFocus({
    invoiceId: currentInvoice.id,
    isLoading,
    sectionConfidence,
    sectionHasError,
    sectionShouldAutoExpand
  });

  const handleSectionToggle = useCallback(
    (sectionId: SectionId) => {
      const isExpanding = !sectionExpanded[sectionId];

      setUserToggledSections((prev) => ({
        ...prev,
        [sectionId]: isExpanding
      }));

      handleSectionExpansionChange(sectionId, isExpanding);
    },
    [handleSectionExpansionChange, sectionExpanded]
  );

  const getAutoExpandReason = (sectionId: SectionId): string | undefined => {
    const sectionTitle = translate(`labels.${sectionId}`);
    if (sectionHasError[sectionId]) {
      return `${sectionTitle}: ${translate('messages.requiredDataMissing')}`;
    }
    const level = sectionConfidence[sectionId];
    if (level === ConfidenceLevel.LOW) {
      return `${sectionTitle}: ${translate('messages.lowConfidence')}`;
    }
    if (level === ConfidenceLevel.MEDIUM) {
      return `${sectionTitle}: ${translate('messages.needsReview')}`;
    }
    return undefined;
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [companies, expenseTypes, currenciesData] = await Promise.all([
        getAllCompanies(),
        getExpenseTypes(Number(currentInvoice.companyId)),
        getCurrencies()
      ]);
      setAllCompanies(companies);
      setExpenseTypeOptions(expenseTypes);
      setCurrencies(currenciesData);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentInvoice.companyId,
    currentInvoice.id,
    getAllCompanies,
    getCurrencies,
    getExpenseTypes
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!documentConfig.allowExpenseType || !expenseTypeOptions.length) {
      return;
    }

    const currentExpenseTypeId = methods.getValues(
      InvoiceVerificationFieldName.ExpenseType
    );
    const isKnownExpenseType = expenseTypeOptions.some(
      (expenseType) => expenseType.id === currentExpenseTypeId
    );

    if (currentExpenseTypeId && !isKnownExpenseType) {
      methods.setValue(InvoiceVerificationFieldName.ExpenseType, '', {
        shouldValidate: true
      });
    }
  }, [expenseTypeOptions, documentConfig.allowExpenseType, methods]);

  // EPN-607: a confirmed Document Data expense type change is persisted
  // immediately through the DOCUMENT_DATA section PUT; the server cascades
  // the new type to every line item and the response refreshes the whole view.
  const handleDocumentExpenseTypeApplied = useCallback(
    async (expenseTypeId: number) => {
      if (!currentInvoice.companyId || !currentInvoice.id) {
        return;
      }
      const typeName =
        expenseTypeOptions.find(
          (expenseType) => expenseType.id === expenseTypeId
        )?.name ?? '';
      try {
        const response = (await saveInvoiceSectionData(
          Number(currentInvoice.companyId),
          Number(currentInvoice.id),
          InvoiceSection.DOCUMENT_DATA,
          { expenseTypeId }
        )) as Invoice;
        showSuccessToastMessage(
          translate(
            (currentInvoice.products ?? []).length === 1
              ? 'messages.expenseTypeAppliedToAllSingle'
              : 'messages.expenseTypeAppliedToAll',
            {
              name: typeName,
              count: String((currentInvoice.products ?? []).length)
            }
          )
        );
        onInvoiceUpdated?.(response);
        setLiveExpenseTypeId(expenseTypeId);
      } catch {
        // the interceptor already showed the error toast
      }
    },
    [
      currentInvoice,
      expenseTypeOptions,
      saveInvoiceSectionData,
      translate,
      onInvoiceUpdated
    ]
  );

  const onSubmit = useCallback(
    async (values: SendForApprovalFormValues) => {
      await sendInvoiceForApprovalById(
        Number(currentInvoice.companyId),
        Number(currentInvoice.id),
        mapInvoiceForSendForApprovalSubmit(
          values,
          currentInvoice,
          dirtyFieldNames
        )
      );
    },
    [currentInvoice, dirtyFieldNames, sendInvoiceForApprovalById]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            ...verificationContentWrapper,
            display: 'flex',
            gap: { xs: 7, lg: 2 },
            flexDirection: {
              lg: 'row',
              xs: 'column-reverse'
            },
            justifyContent: 'space-between',
            height: 'calc(100vh - 200px)',
            overflow: 'hidden'
          }}
        >
          <InvoiceFilePreview currentInvoice={currentInvoice} />

          <Box
            sx={{
              width: { lg: '50%', xs: '100%' },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || isSubmitting}
              >
                {translate('buttons.sendForApproval')}
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflow: 'auto',
                flex: 1
              }}
            >
              {overallConfidence && (
                <OverallConfidenceBanner result={overallConfidence} />
              )}
              <div
                id="invoice-verification-live-region"
                aria-live="polite"
                aria-atomic="true"
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden'
                }}
              />

              <Box ref={getSectionRef('initiation')}>
                <InitiationCard
                  allCompanies={allCompanies}
                  currentInvoice={currentInvoice}
                  documentConfig={documentConfig}
                  confidenceLevel={initiationConfidence}
                  expanded={sectionExpanded.initiation}
                  onToggle={() => handleSectionToggle('initiation')}
                  onExpanded={() => handleSectionExpanded('initiation')}
                  autoExpandReason={
                    userToggledSections.initiation === undefined
                      ? getAutoExpandReason('initiation')
                      : undefined
                  }
                  hasError={sectionHasError.initiation}
                  onReceiverChange={handleReceiverChange}
                />
              </Box>

              <Box ref={getSectionRef('counterparty')}>
                <CounterpartyCard
                  currentInvoice={currentInvoice}
                  confidenceLevel={counterpartyConfidence}
                  expanded={sectionExpanded.counterparty}
                  onToggle={() => handleSectionToggle('counterparty')}
                  onExpanded={() => handleSectionExpanded('counterparty')}
                  autoExpandReason={
                    userToggledSections.counterparty === undefined
                      ? getAutoExpandReason('counterparty')
                      : undefined
                  }
                  hasError={sectionHasError.counterparty}
                />
              </Box>

              <Box ref={getSectionRef('reimbursement')}>
                <ReimbursementCard
                  currentInvoice={currentInvoice}
                  confidenceLevel={reimbursementConfidence}
                  expanded={sectionExpanded.reimbursement}
                  onToggle={() => handleSectionToggle('reimbursement')}
                  onExpanded={() => handleSectionExpanded('reimbursement')}
                  autoExpandReason={
                    userToggledSections.reimbursement === undefined
                      ? getAutoExpandReason('reimbursement')
                      : undefined
                  }
                  hasError={sectionHasError.reimbursement}
                />
              </Box>

              {!isReimbursement && (
                <Box ref={getSectionRef('documentPaymentStatus')}>
                  <DocumentPaymentStatusSection
                    currentInvoice={currentInvoice}
                    onExpanded={() =>
                      handleSectionOpened('documentPaymentStatus')
                    }
                  />
                </Box>
              )}

              <Box ref={getSectionRef('documentData')}>
                <DocumentDataCard
                  currentInvoice={currentInvoice}
                  expenseTypeOptions={expenseTypeOptions}
                  currencies={currencies}
                  documentConfig={documentConfig}
                  confidenceLevel={documentDataConfidence}
                  expanded={sectionExpanded.documentData}
                  onToggle={() => handleSectionToggle('documentData')}
                  onExpanded={() => handleSectionExpanded('documentData')}
                  autoExpandReason={
                    userToggledSections.documentData === undefined
                      ? getAutoExpandReason('documentData')
                      : undefined
                  }
                  hasError={sectionHasError.documentData}
                  onExpenseTypeChange={setLiveExpenseTypeId}
                  onExpenseTypeApplied={handleDocumentExpenseTypeApplied}
                />
              </Box>

              <Box ref={getSectionRef('lineItems')}>
                <LineItemsCard
                  currentInvoice={currentInvoice}
                  expenseTypeOptions={expenseTypeOptions}
                  documentExpenseTypeId={
                    liveExpenseTypeId ?? currentInvoice.expenseType?.id
                  }
                  onInvoiceUpdated={onInvoiceUpdated}
                  liveDocumentAmountEur={liveDocumentAmountEur}
                  expanded={sectionExpanded.lineItems}
                  onToggle={() => handleSectionToggle('lineItems')}
                  onExpanded={() => handleSectionExpanded('lineItems')}
                  autoExpandReason={
                    userToggledSections.lineItems === undefined
                      ? getAutoExpandReason('lineItems')
                      : undefined
                  }
                />
              </Box>

              <Box ref={getSectionRef('commentsAndTags')}>
                <CommentsAndTagsCard
                  currentInvoice={currentInvoice}
                  onExpanded={() => handleSectionOpened('commentsAndTags')}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
